declare global {
  interface Window {
    __CNTNUS_GPC_ACTIVE__?: boolean;
  }
}

export function LegalFooter() {
  const gpcActive = typeof window !== "undefined" && Boolean(window.__CNTNUS_GPC_ACTIVE__);
  return (
    <footer className="app-footer" role="contentinfo">
      <div className="app-footer-links">
        <a href="/terms" target="_blank" rel="noreferrer">Terms</a>
        <a href="/privacy" target="_blank" rel="noreferrer">Privacy</a>
        <a href="/security" target="_blank" rel="noreferrer">Security</a>
      </div>
      {gpcActive && (
        <span className="gpc-badge" title="Global Privacy Control signal detected and honored">
          ● Privacy Signal Honored (GPC)
        </span>
      )}
    </footer>
  );
}
import { FrondEngine, exportZoomVideo } from "@frond/engine";
import { DocumentMeta } from "@frond/client";
import { useEffect, useState } from "react";
import { store, useAppState } from "../state/store";
import { downloadBlob } from "../state/tools";
import { APP_VERSION, BUILD_TIME, GIT_SHA } from "../version";

/* ------------------------------- Gallery -------------------------------- */

export function GalleryModal({ onClose }: { onClose: () => void }) {
  const state = useAppState();
  const [cloudDocs, setCloudDocs] = useState<DocumentMeta[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (state.client && state.auth.status === "signed-in") {
      state.client
        .listDocuments()
        .then((r) => !cancelled && setCloudDocs(r.items))
        .catch(() => !cancelled && setCloudDocs([]));
    } else {
      setCloudDocs([]);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.auth.status, state.booted]);

  const newDoc = () => {
    void store.deleteLocal(null).then(() => {
      window.location.reload();
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Gallery</h2>
        <p className="panel-hint">
          Canvases are organized spatially inside each document; the gallery lists documents.
          {state.auth.status !== "signed-in" && " Sign in to sync canvases to the cloud."}
        </p>
        <div className="gallery-actions-bar">
          <button className="btn" onClick={newDoc}>
            ï¼‹ New canvas
          </button>
          <button
            className="btn btn-primary"
            onClick={() => void store.saveNow()}
            disabled={state.doc.saveState === "saving"}
          >
            {state.doc.saveState === "saving" ? "â³ Savingâ€¦" : "ðŸ’¾ Save current canvas"}
          </button>
        </div>

        {state.auth.status !== "signed-in" && (
          <div className="gallery-auth-promo">
            <p>Signed out: Canvases are saved locally in this browser.</p>
            <div className="promo-buttons">
              <button className="btn subtle" onClick={() => void store.signIn()}>
                Sign in
              </button>
              <button className="btn btn-accent" onClick={() => void store.signUp()}>
                Sign up
              </button>
            </div>
          </div>
        )}

        <div className="doc-list">
          <div className="doc-list-head">In the cloud</div>
          {cloudDocs === null && <p className="panel-hint">Loadingâ€¦</p>}
          {cloudDocs !== null && cloudDocs.length === 0 && (
            <p className="panel-hint">No cloud canvases yet.</p>
          )}
          {cloudDocs?.map((d) => (
            <div className="panel-row" key={d.id}>
              <button
                className="panel-row-main"
                disabled={busy === d.id}
                onClick={() => {
                  setBusy(d.id);
                  void store.openDocument(d.id).finally(() => {
                    setBusy(null);
                    onClose();
                  });
                }}
              >
                <span className="bm-label">{d.name}</span>
                <span className="bm-depth">{formatBytes(d.sizeBytes)}</span>
              </button>
              <button
                className="icon-btn"
                title="Delete"
                onClick={() => {
                  setBusy(d.id);
                  void store.deleteDocument(d.id).finally(() => setBusy(null));
                  setCloudDocs((prev) => (prev ? prev.filter((x) => x.id !== d.id) : prev));
                }}
              >
                âœ•
              </button>
            </div>
          ))}
        </div>
        <div className="modal-actions">
          <button className="btn subtle" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- Export --------------------------------- */

export function ExportModal({ engine, onClose }: { engine: FrondEngine; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);

  const png = (scale: number, label: string) => async () => {
    setBusy(true);
    try {
      const blob = await engine.exportPng(scale);
      downloadBlob(blob, `${slug()}-${label}.png`);
    } finally {
      setBusy(false);
    }
  };

  const svg = () => {
    const blob = new Blob([engine.exportSvg()], { type: "image/svg+xml" });
    downloadBlob(blob, `${slug()}.svg`);
  };

  const json = () => {
    const blob = new Blob([engine.serialize()], { type: "application/json" });
    downloadBlob(blob, `${slug()}.frond.json`);
  };

  const video = async () => {
    setBusy(true);
    setProgress(0);
    try {
      const cam = engine.camera;
      const blob = await exportZoomVideo(engine, {
        target: { cellId: cam.focusCellId, x: cam.x, y: cam.y },
        durationMs: 6000,
        zoomInFactor: 64,
        onProgress: (t) => setProgress(Math.round(t * 100)),
      });
      downloadBlob(blob, `${slug()}-zoom.webm`);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Export</h2>
        <div className="export-grid">
          <button className="btn block" disabled={busy} onClick={() => void png(1, "viewport")()}>
            PNG â€” viewport
          </button>
          <button className="btn block" disabled={busy} onClick={() => void png(4, "hi-res")()}>
            PNG â€” high resolution (4Ã—)
          </button>
          <button className="btn block" disabled={busy} onClick={svg}>
            SVG â€” whole canvas (vector, deep zoom)
          </button>
          <button className="btn block" disabled={busy} onClick={json}>
            Frond document (JSON)
          </button>
          <button className="btn block" disabled={busy} onClick={() => void video()}>
            Zoom video (WebM) {progress !== null && `â€” ${progress}%`}
          </button>
        </div>
        <p className="panel-hint">
          The SVG keeps every zoom level as nested vector groups â€” you can open it in any editor
          and keep zooming in.
        </p>
        <div className="modal-actions">
          <button className="btn subtle" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- Share ---------------------------------- */

export function ShareModal({ engine, onClose }: { engine: FrondEngine; onClose: () => void }) {
  const state = useAppState();
  const [enabled, setEnabled] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (state.doc.id && state.client && state.auth.status === "signed-in") {
      state.client
        .getDocument(state.doc.id)
        .then((ref) => {
          if (cancelled) return;
          setEnabled(ref.meta.published?.enabled ?? false);
          setUrl(ref.meta.published?.url ?? null);
        })
        .catch(() => !cancelled && setError("Could not load sharing state."));
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.doc.id]);

  const toggle = async () => {
    if (!state.client || !state.doc.id) return;
    setError(null);
    try {
      const meta = await state.client.publish(state.doc.id, !enabled);
      setEnabled(meta.published?.enabled ?? false);
      setUrl(meta.published?.url ?? null);
    } catch {
      setError("Publishing failed. Save the canvas first, then retry.");
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Share</h2>
        <p className="panel-hint">
          Publish a read-only, infinitely zoomable web view of this canvas (like Endless Paper's
          "publish your canvas as an interactive website").
        </p>
        {state.auth.status !== "signed-in" && (
          <button className="btn" onClick={() => void store.signIn()}>
            Sign in to publish
          </button>
        )}
        {state.auth.status === "signed-in" && (
          <>
            {!state.doc.id && <p className="panel-hint">Save the canvas to the cloud first (it saves automatically).</p>}
            {state.doc.id && (
              <label className="check-row">
                <input type="checkbox" checked={enabled} onChange={() => void toggle()} />
                <span>Public read-only link</span>
              </label>
            )}
            {enabled && url && (
              <div className="share-url">
                <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
                <button className="btn" onClick={() => void navigator.clipboard?.writeText(url)}>
                  Copy
                </button>
              </div>
            )}
          </>
        )}
        {error && <p className="panel-error">{error}</p>}
        <div className="modal-actions">
          <button className="btn subtle" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Auth ---------------------------------- */

export function AuthModal({ onClose }: { onClose: () => void }) {
  const state = useAppState();
  const [devEmail, setDevEmail] = useState("dev-user@example.com");

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>AWS Cognito Account</h2>
        <p className="panel-hint">
          Sign in or create an account with AWS Cognito to save your canvases across devices,
          access revisions, and publish interactive links.
        </p>

        <div className="auth-modal-buttons">
          <button
            className="btn btn-primary block"
            onClick={() => {
              void store.signIn();
            }}
          >
            Sign in with AWS Cognito
          </button>
          <button
            className="btn block"
            onClick={() => {
              void store.signUp();
            }}
          >
            Sign up (Create account)
          </button>
        </div>

        {(state.configError ||
          (typeof window !== "undefined" &&
            (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"))) && (
          <div className="dev-auth-box">
            <div className="dev-auth-title">ðŸ› ï¸ Local Development & Testing</div>
            <p className="panel-hint">
              Testing locally without active AWS credentials? Sign in with a local test account:
            </p>
            <div className="dev-auth-row">
              <input
                type="email"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                placeholder="dev@example.com"
              />
              <button
                className="btn subtle"
                onClick={() => {
                  store.signInDev(devEmail);
                  onClose();
                }}
              >
                Mock Sign In
              </button>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn subtle" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- About --------------------------------- */

export function AboutModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>About Frond</h2>
        <p className="panel-hint">An infinite-zoom drawing canvas.</p>
        <div className="panel-list">
          <div className="panel-row">
            <span className="bm-label">App version</span>
            <span className="bm-depth">{APP_VERSION}</span>
          </div>
          <div className="panel-row">
            <span className="bm-label">Commit</span>
            <span className="bm-depth">{GIT_SHA}</span>
          </div>
          <div className="panel-row">
            <span className="bm-label">Built</span>
            <span className="bm-depth">{BUILD_TIME}</span>
          </div>
        </div>
        <p className="panel-hint">
          If the text tool (or anything else) misbehaves, compare this commit hash with the
          latest on main â€” a stale dev deploy is the most common cause.
        </p>
        <div className="modal-actions">
          <button className="btn subtle" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- helpers --------------------------------- */

function slug(): string {
  const d = new Date();
  return `frond-${d.toISOString().slice(0, 19).replace(/[:T]/g, "-")}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
